/**
 * Modal — wrapper generique reutilisable.
 *
 * Remplace tout `window.confirm/alert/prompt` (bloque par ESLint
 * `no-restricted-globals`) et tout markup ad-hoc d'overlay.
 *
 * Usage :
 *   <Modal isOpen={isOpen} onClose={onClose} title="Mon titre" size="form">
 *     <Modal.Body>contenu</Modal.Body>
 *     <Modal.Footer>
 *       <Button variant="ghost" onClick={onClose}>Annuler</Button>
 *       <Button variant="primary">OK</Button>
 *     </Modal.Footer>
 *   </Modal>
 *
 * Avec form (l'event Enter du form valide automatiquement) :
 *   <Modal isOpen={open} onClose={close} title="Creer">
 *     <form onSubmit={handleSubmit(onValid)}>
 *       <Modal.Body>fields...</Modal.Body>
 *       <Modal.Footer>buttons...</Modal.Footer>
 *     </form>
 *   </Modal>
 *
 * Tailles : 'small' | 'form' | 'large'. Pour des cas tres specifiques,
 * utiliser le prop `className` plutot qu'inventer une nouvelle taille.
 *
 * Adapte de scapino-portal/frontend/src/components/ui/Modal.tsx.
 */

import { useEffect, useId, type ReactNode } from 'react'

import './Modal.css'

export type ModalSize = 'small' | 'form' | 'large'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: ReactNode
  /**
   * Petite etiquette contextuelle au-dessus du titre (uppercase, muted).
   * Sert a donner le contexte de l'entite ouverte sans alourdir le titre.
   * Ex : `kicker="Œuvre · Lampes du soir"`.
   */
  kicker?: ReactNode
  size?: ModalSize
  children: ReactNode
  className?: string
  /** Actions custom dans le header (a cote du bouton fermer). */
  headerActions?: ReactNode
  /** Cache le bouton fermer (pour les modals non skippables). */
  hideClose?: boolean
  /** ARIA label si pas de titre visible. */
  ariaLabel?: string
}

function ModalBody({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={`modal-body${className ? ` ${className}` : ''}`}>{children}</div>
}

function ModalFooter({ children, leftActions }: { children: ReactNode; leftActions?: ReactNode }) {
  return (
    <div className={`modal-footer${leftActions ? ' modal-footer--split' : ''}`}>
      {leftActions && <div className="modal-footer-left">{leftActions}</div>}
      <div className="modal-footer-actions">{children}</div>
    </div>
  )
}

export function Modal({
  isOpen,
  onClose,
  title,
  kicker,
  size,
  children,
  className,
  headerActions,
  hideClose = false,
  ariaLabel,
}: ModalProps) {
  // Escape key handler
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isOpen, onClose])

  const reactId = useId()
  const titleId = `modal-title-${reactId}`

  if (!isOpen) return null

  const sizeClass = size ? `modal-content--${size}` : ''
  const contentClasses = ['modal-content', sizeClass, className].filter(Boolean).join(' ')
  const hasVisibleTitle = title != null

  return (
    <div className="modal-overlay">
      <button
        type="button"
        className="modal-backdrop"
        aria-label="Fermer la fenêtre"
        onClick={onClose}
      />
      <div
        className={contentClasses}
        role="dialog"
        aria-modal="true"
        aria-labelledby={hasVisibleTitle ? titleId : undefined}
        aria-label={hasVisibleTitle ? undefined : ariaLabel}
      >
        {hasVisibleTitle && (
          <div className="modal-header">
            <div className="modal-header-title-group">
              {kicker && <div className="modal-kicker">{kicker}</div>}
              <h2 id={titleId} className="modal-title">
                {title}
              </h2>
            </div>
            <div className="modal-header-actions">
              {headerActions}
              {!hideClose && (
                <button type="button" className="modal-close" onClick={onClose} aria-label="Fermer">
                  ×
                </button>
              )}
            </div>
          </div>
        )}
        {children}
      </div>
    </div>
  )
}

Modal.Body = ModalBody
Modal.Footer = ModalFooter

export default Modal
